
import { useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';

/**
 * Custom hook to handle event deadline logic
 * 
 * @param deadline The event deadline timestamp
 * @param guestRsvpComplete Whether all guests have completed their RSVP
 * @param hasRecommendations Whether the event has date/venue recommendations
 * @returns Deadline status information
 */
export function useEventDeadline(
  deadline: string | null,
  guestRsvpComplete = false,
  hasRecommendations = false
) {
  return useMemo(() => {
    // If no deadline is set, don't show anything
    if (!deadline) {
      return {
        showBadge: false,
        isExpired: false,
        timeRemaining: '',
      };
    }
    
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const isExpired = deadlineDate < now;
    
    // If deadline has passed, only show badge if guests haven't all RSVP'd and no recommendations exist
    if (isExpired) {
      const showBadge = !guestRsvpComplete && !hasRecommendations;
      return {
        showBadge,
        isExpired: true,
        timeRemaining: '',
      };
    }
    
    // For active deadlines, format the remaining time
    const timeRemaining = formatDistanceToNow(deadlineDate, { 
      addSuffix: false
    }).replace(/^about /i, ''); // Remove "about " prefix
    
    return {
      showBadge: true,
      isExpired: false,
      timeRemaining,
    };
  }, [deadline, guestRsvpComplete, hasRecommendations]);
}
